import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ActionDto, ChanceNodeActionDto } from "@api";
import { ModalController, SelectCustomEvent, TextareaCustomEvent } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { Observable, Subscription } from "rxjs";
import { first } from "rxjs/operators";
import { RootState } from "src/app/store";
import * as ScenarioActions from "src/app/store/scenario/actions";
import * as FromScenario from "src/app/store/scenario/selectors";

@Component({
  selector: "app-action-selector",
  templateUrl: "./action-selector.page.html",
  styleUrls: ["./action-selector.page.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActionSelectorPage implements OnInit, OnDestroy {
  @Input() public chanceNodeId: string;
  @Input() public reason: boolean;
  @Input() public chanceNodeAction?: ChanceNodeActionDto;
  @Input() public actionId?: ActionDto;
  @Input() public parameters? = {};

  public form: FormGroup;
  public jsonError?: string;
  public loading$: Observable<boolean> = this.store.select(FromScenario.getLoading);
  public actions$: Observable<ActionDto[]> = this.store.select(FromScenario.getActions);

  private $actionId: Subscription;

  constructor(private store: Store<RootState>, private modalController: ModalController) {}

  public ngOnInit(): void {
    this.form = new FormGroup({
      actionId: new FormControl(this.actionId, [Validators.required]),
    });

    if (this.chanceNodeAction) this.onActionChanged(this.chanceNodeAction.actionId);
    this.$actionId = this.form.get("actionId").valueChanges.subscribe(this.onActionChanged.bind(this));
  }

  public ngOnDestroy(): void {
    this.$actionId?.unsubscribe();
  }

  public dismiss(): void {
    this.modalController.dismiss();
  }

  public onActionTypeChanges(_: SelectCustomEvent): void {
    this.jsonError = null;
  }

  public onJSONChanges(event: TextareaCustomEvent): void {
    try {
      JSON.parse(event.target.value);
      this.jsonError = null;
    } catch (e) {
      this.jsonError = e.message;
    }
  }

  public save(): void {
    const { actionId, ...parameters } = this.form.value;
    if (this.chanceNodeAction) {
      this.store.dispatch(
        ScenarioActions.editChanceNodeAction({
          chanceNodeActionId: this.chanceNodeAction.id,
          chanceNodeId: this.chanceNodeId,
          updateChanceNodeActionDto: {
            actionId,
            parameters,
            reason: this.reason,
          },
        }),
      );
    } else {
      this.store.dispatch(
        ScenarioActions.addActionToChanceNode({
          chanceNodeId: this.chanceNodeId,
          createChanceNodeActionDto: {
            actionId,
            parameters,
            reason: this.reason,
          },
        }),
      );
    }
  }

  public async remove(): Promise<void> {
    this.store.dispatch(
      ScenarioActions.deleteChanceNodeAction({ chanceNodeId: this.chanceNodeId, chanceNodeActionId: this.chanceNodeAction.id }),
    );
  }

  private onActionChanged(actionId: string): void {
    this.actions$.pipe(first()).subscribe(actions => {
      const action = actions.find(a => a.id === actionId);

      action.parameters.forEach(parameter => {
        const control = new FormControl();
        control.patchValue(this.chanceNodeAction ? this.chanceNodeAction?.parameters[parameter.name] : parameter.defaultValue);
        if (parameter.required) control.addValidators(Validators.required);
        this.form.addControl(parameter.name, control, { emitEvent: false });
      });
    });
  }
}
